import { useTheme } from "@emotion/react";
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons";
import { useStore } from "@tanstack/react-form";
import Box from "@ternary/api-lib/ui-lib/components/Box";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Icon from "@ternary/api-lib/ui-lib/components/Icon";
import Text from "@ternary/api-lib/ui-lib/components/Text";
import { Tooltip } from "@ternary/api-lib/ui-lib/components/Tooltip";
import React from "react";
import Switch from "../../../../ui-lib/components/Switch";
import copyText from "../../copyText";
import { BillingRuleTable } from "../BillingRuleTable";
import { Form } from "./types";

interface Props {
  form: Form;
  isLoadingBillingRules: boolean;
}

export function BillingRuleFormPriority(props: Props) {
  const theme = useTheme();

  const { form } = props;

  const formState = useStore(form.store, (state) => ({
    setRulePriority: state.values.setRulePriority,
  }));

  return (
    <Flex direction="column" gap={theme.space_xs}>
      <Box>
        <Flex alignItems="center" gap={theme.space_xs}>
          <Text fontSize="18px">{copyText.rulePrioritySectionTitle}</Text>
          <Tooltip
            content={copyText.rulePriorityTooltipMessage}
            placement="bottom"
            width="250px"
          >
            <Icon color={"#535862"} icon={faInfoCircle} />
          </Tooltip>
        </Flex>
        <Text color="#7C8591">{copyText.rulePrioritySectionCaption}</Text>
      </Box>
      <Box
        backgroundColor={theme.panel_backgroundColor}
        borderRadius={theme.borderRadius_2}
        padding={theme.space_md}
      >
        <Flex
          alignItems="center"
          justifyContent="space-between"
          marginBottom={formState.setRulePriority ? theme.space_md : 0}
        >
          <Text fontSize={theme.fontSize_base}>
            {copyText.setRulePriorityInputLabel}
          </Text>
          <form.Field
            name="setRulePriority"
            children={(field) => {
              return (
                <Switch
                  checked={field.state.value}
                  onChange={(checked) => field.handleChange(checked)}
                />
              );
            }}
          />
        </Flex>
        <form.Subscribe
          selector={(state) => state.values.name}
          children={(name) => {
            return (
              <form.Field
                name="minimalRules"
                children={(field) => {
                  if (!formState.setRulePriority) return null;

                  const minimalRules = field.state.value.map((rule) =>
                    rule.id === "new" ? { ...rule, name } : rule
                  );

                  return (
                    <BillingRuleTable
                      isLoading={props.isLoadingBillingRules}
                      minimal
                      rules={minimalRules}
                      onInteraction={(interaction) => {
                        if (
                          interaction.type !==
                          BillingRuleTable.INTERACTION_ROW_ORDER_CHANGED
                        ) {
                          return;
                        }

                        const oldIndex = interaction.oldIndex;
                        const newIndex = interaction.newIndex;

                        const fromObject = field.state.value[oldIndex];
                        const toObject = field.state.value[newIndex];

                        if (!fromObject || !toObject) return;

                        // Create new array with reordered items
                        const newRules = [...field.state.value];

                        newRules.splice(oldIndex, 1); // Remove from old position
                        newRules.splice(newIndex, 0, fromObject); // Insert at new position

                        // Update priorities based on new order
                        const updatedRules = newRules.map((rule, index) => ({
                          ...rule,
                          priority: index + 1,
                        }));

                        field.handleChange(updatedRules);
                      }}
                    />
                  );
                }}
              />
            );
          }}
        />
      </Box>
    </Flex>
  );
}
